import { usePollingStatus, PollStatusDto } from '@/hooks/integrations';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Link } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

const PollingMonitorIntervalMs = 2500;

const PollingMonitor = () => {
  const { data, isLoading, error, dataUpdatedAt } = usePollingStatus(PollingMonitorIntervalMs);
  const [history, setHistory] = useState<{ time: string; due: number }[]>([]);
  const [recentPolls, setRecentPolls] = useState<
    (PollStatusDto & { status: 'pending' | 'processed'; seenAt: Date })[]
  >([]);

  useEffect(() => {
    if (data) {
      setHistory((prev) => {
        const now = new Date();
        const timeString = now.toLocaleTimeString();
        const newPoint = { time: timeString, due: data.duePolls };
        // Keep last 30 seconds at PollingMonitorIntervalMs interval)
        const newHistory = [...prev, newPoint];
        const maxPoints = Math.ceil(30000 / PollingMonitorIntervalMs);
        if (newHistory.length > maxPoints) {
          return newHistory.slice(newHistory.length - maxPoints);
        }
        return newHistory;
      });

      if (data.oldestPoll) {
        setRecentPolls((prev) => {
          const current = data.oldestPoll!;
          
          // If the list is empty, just add it
          if (prev.length === 0) {
            return [{ ...current, status: 'pending', seenAt: new Date() }];
          }
  
          const [latest] = prev;
          
          // If it's the same ID, it's still pending.
          if (latest.id === current.id) {
               return prev;
          }
  
          // If it's a different ID, the previous one is processed.
          const newPoll = { ...current, status: 'pending' as const, seenAt: new Date() };
          
          const updatedPrev = prev.map(p => 
              p.status === 'pending' ? { ...p, status: 'processed' as const } : p
          );
  
          return [newPoll, ...updatedPrev].slice(0, 5);
        });
      } else {
          // No oldest poll means queue is empty?
          // Mark all as processed
          setRecentPolls(prev => {
              if (prev.every(p => p.status === 'processed')) return prev;
              return prev.map(p => ({ ...p, status: 'processed' as const }));
          });
      }
    }
  }, [data]);

  if (error) {
    return (
      <div className="p-4 text-red-500">
        Error loading polling status: {error.message}
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 h-full overflow-y-auto">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-white">Polling Monitor</h1>
      </div>

        <div>
        <h3 className="font-semibold text-white mb-1">How Polling Works</h3>
        <p>
            The Integration Hub provides a polling mechanism to activate integrations without a external event. 
            Each integration registers polling tasks with a specific interval. 
            The system uses a sorted set in Redis to manage the schedule. Working correctly, the due polls will never accumulate.
        </p>
        </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Polls</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading && !data ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <div className="text-2xl font-bold">{data?.totalPolls ?? 0}</div>
            )}
            <p className="text-xs text-muted-foreground mt-1">
              Total number of active polling tasks in the system.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Due Polls</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading && !data ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <div className="text-2xl font-bold">{data?.duePolls ?? 0}</div>
            )}
            <p className="text-xs text-muted-foreground mt-1">
              Number of polls that are currently ready to be executed but haven't been picked up yet.
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
          <Card className="col-span-1">
            <CardHeader>
              <CardTitle>Oldest Poll</CardTitle>
              <CardDescription>Details of the poll waiting longest to be executed</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {isLoading && !data ? (
                <div className="space-y-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                </div>
              ) : data?.oldestPoll ? (
                <div className="grid grid-cols-[auto_1fr] gap-4 text-sm">
                  <span className="font-medium text-muted-foreground">ID:</span>
                  <span className="font-mono">{data.oldestPoll.id}</span>
                  
                  <span className="font-medium text-muted-foreground">Device ID:</span>
                  <span className="font-mono">{data.oldestPoll.deviceId}</span>
                  <span className="font-medium text-muted-foreground">Next Run:</span>
                  <div className="flex flex-col">
                    <span className="font-mono">
                      {new Date(data.oldestPoll.nextRun * 1000).toLocaleString()}
                    </span>
                    <span
                      className={cn(
                        'text-xs',
                        dataUpdatedAt / 1000 - data.oldestPoll.nextRun > 5
                          ? 'text-red-500'
                          : 'text-muted-foreground'
                      )}
                    >
                      {Math.floor(dataUpdatedAt / 1000 - data.oldestPoll.nextRun)}s ago (as of request)
                    </span>
                  </div>

                  <span className="font-medium text-muted-foreground">Interval:</span>
                  <span>{data.oldestPoll.intervalSeconds}s</span>
                  
                  <span className="font-medium text-muted-foreground ">Metadata:</span>
                  {data.oldestPoll.metadata ?
                  <pre className="text-xs bg-primary /50 p-2 rounded overflow-auto max-h-40 col-span-2 whitespace-pre-wrap break-all font-mono border border-border">
                    {data.oldestPoll.metadata}
                  </pre> : <span className='text-muted-foreground font-mono'>null</span>}
                  
                </div>
              ) : (
                <div className="text-muted-foreground text-sm">No polls currently due.</div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Recent Oldest Polls</CardTitle>
              <CardDescription>History of processed items</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recentPolls.map((poll) => (
                  <div key={`${poll.id}-${poll.seenAt.getTime()}`} className="flex items-center justify-between space-x-4">
                    <div className="flex flex-col space-y-1">
                      <span className={cn("text-sm font-medium leading-none", poll.status === 'processed' && "line-through text-muted-foreground")}>
                        {poll.deviceId}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {poll.seenAt.toLocaleTimeString()}
                      </span>
                    </div>
                    <div className={cn("text-xs px-2 py-1 rounded-full", 
                      poll.status === 'pending' ? "bg-yellow-500/20 text-yellow-500" : "bg-green-500/20 text-green-500"
                    )}>
                      {poll.status}
                    </div>
                  </div>
                ))}
                {recentPolls.length === 0 && (
                  <div className="text-sm text-muted-foreground">Waiting for data...</div>
                )}
              </div>
            </CardContent>
          </Card>
      </div>

        <Card className="col-span-1">
          <CardHeader>
            <CardTitle>Due Polls History</CardTitle>
            <CardDescription>Real-time tracking of due polls count</CardDescription>
          </CardHeader>
          <CardContent className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={history}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="time" stroke="#888" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#888" fontSize={12} tickLine={false} axisLine={false} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#111', border: '1px solid #333' }}
                  itemStyle={{ color: '#fff' }}
                />
                <Line
                  type="monotone"
                  dataKey="due"
                  stroke="#84cc16" // lime-500
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>


    </div>
  );
};

export default PollingMonitor;
